import { trpc } from '@/lib/trpc';

type SessionFeeling = 'crushed_it' | 'solid' | 'tough' | 'cooked';

const FEELING_META: Record<SessionFeeling, { emoji: string; label: string; color: string; barColor: string }> = {
  crushed_it: { emoji: '🔥', label: 'Crushed It', color: 'text-orange-400', barColor: 'bg-orange-400' },
  solid:       { emoji: '✅', label: 'Solid',      color: 'text-emerald-400', barColor: 'bg-emerald-400' },
  tough:       { emoji: '😤', label: 'Tough',      color: 'text-yellow-400', barColor: 'bg-yellow-400' },
  cooked:      { emoji: '💀', label: 'Cooked',     color: 'text-red-400', barColor: 'bg-red-400' },
};

export function RecoveryStatusWidget() {
  const { data: sessions = [] } = trpc.sessions.list.useQuery();

  const completedWithFeelings = sessions
    .filter((s) => !!s.completedAt && !!(s as any).sessionFeeling)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const lastSession = completedWithFeelings[0];
  const lastFeeling = lastSession ? ((lastSession as any).sessionFeeling as SessionFeeling) : null;
  const lastNudge = lastSession ? ((lastSession as any).recoveryNudge as string | null) : null;

  // 4-week feeling trend (last 28 days)
  const now = new Date();
  const fourWeeksAgo = new Date(now.getTime() - 28 * 24 * 3600 * 1000);
  const recentFeelings = completedWithFeelings.filter(
    (s) => new Date(s.date) >= fourWeeksAgo
  );

  // Count feelings
  const feelingCounts: Record<SessionFeeling, number> = {
    crushed_it: 0, solid: 0, tough: 0, cooked: 0,
  };
  recentFeelings.forEach((s) => {
    const f = (s as any).sessionFeeling as SessionFeeling;
    if (f in feelingCounts) feelingCounts[f]++;
  });
  const total = recentFeelings.length;

  if (completedWithFeelings.length === 0) return null;

  return (
    <div className="forge-panel p-4 rounded-xl border border-border/40 bg-card/30">
      <div className="font-mono text-[9px] text-primary uppercase tracking-widest mb-3">
        RECOVERY STATUS
      </div>

      {/* Last session feeling */}
      {lastFeeling && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{FEELING_META[lastFeeling].emoji}</span>
          <div className="min-w-0">
            <div className={`font-mono text-xs font-bold uppercase tracking-wider ${FEELING_META[lastFeeling].color}`}>
              {FEELING_META[lastFeeling].label}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {lastSession?.name ?? 'Last session'}
            </div>
          </div>
        </div>
      )}

      {/* Last nudge */}
      {lastNudge && (
        <div className="text-[11px] text-muted-foreground leading-relaxed mb-3 border-l-2 border-primary/30 pl-2">
          {lastNudge}
        </div>
      )}

      {/* 4-week trend bars */}
      {total > 0 && (
        <div>
          <div className="font-mono text-[8px] text-muted-foreground uppercase tracking-widest mb-1.5">
            4-WEEK FEELING TREND ({total} sessions)
          </div>
          <div className="space-y-1">
            {(Object.keys(feelingCounts) as SessionFeeling[]).map((f) => {
              const count = feelingCounts[f];
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const meta = FEELING_META[f];
              return (
                <div key={f} className="flex items-center gap-2">
                  <span className="text-sm w-4 text-center">{meta.emoji}</span>
                  <div className="flex-1 h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${meta.barColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-[9px] text-muted-foreground w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
